import { Flex, Skeleton, Typography, Popover, DatePicker } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getActivity, getCurrentEmi } from 'shared/config/store/selectors/app';
import { RootState } from 'shared/config/store/store';
import { getEMIMonthSpend } from 'shared/config/store/reducers/accountsSlice';
import classes from '../ui/Transactions.module.scss';
import { useGetMonthSpendQuery } from '../api';
import { CalendarOutlined } from '@ant-design/icons';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

export const MonthSpending = ({ 
  accountId, 
  start,
  onDateRangeChange,
  showCalendar = true
}: { 
  accountId: number, 
  start: string,
  onDateRangeChange?: (startDate: string, endDate: string | undefined) => void,
  showCalendar?: boolean
}) => {
  const { formatMessage: t } = useIntl();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  
  // Determine start and end dates
  const startDate = dateRange?.[0] ? dateRange[0].format('DD-MM-YYYY') : start;
  const endDate = dateRange?.[1] ? dateRange[1].format('DD-MM-YYYY') : undefined;
  
  useGetMonthSpendQuery({ accountId, start: startDate, end: endDate });
  const currentEmi = useSelector(getCurrentEmi);
  const monthSpend = useSelector((state: RootState) => getEMIMonthSpend(state, currentEmi));
  const currentMonthSpend = monthSpend?.[String(accountId)] || 0;
  const activity = useSelector(getActivity);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
      setPopoverOpen(false);
      const startDateStr = dates[0].format('DD-MM-YYYY');
      const endDateStr = dates[1].format('DD-MM-YYYY');
      onDateRangeChange?.(startDateStr, endDateStr);
    } else {
      setDateRange(null);
      onDateRangeChange?.(start, undefined);
    }
  };

  const getDisplayText = () => {
    if (dateRange?.[0] && dateRange?.[1]) {
      // Format dates as DD.MM.YYYY (e.g., "01.09.2024", "31.10.2024")
      const startFormatted = dateRange[0].format('DD.MM.YYYY');
      const endFormatted = dateRange[1].format('DD.MM.YYYY');
      return t(
        { id: 'transactions.spent.period' },
        { start: startFormatted, end: endFormatted }
      );
    }
    return t(
      { id: 'transactions.spent' },
      { month: t({ id: `calendar.month.${new Date().getMonth()}` }) }
    );
  };

  const datePickerContent = (
    <DatePicker.RangePicker
      onChange={handleDateRangeChange}
      value={dateRange}
      format="DD.MM.YYYY"
      disabledDate={(current: Dayjs) => current && current.isAfter(dayjs())}
    />
  );

  return (
    <Flex justify='space-between' align='center'>
      <Flex align='center' gap={8}>
        {showCalendar && (
          <Popover
            content={datePickerContent}
            trigger="click"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            placement="bottomLeft"
          >
            <CalendarOutlined 
              style={{ fontSize: 18, cursor: 'pointer', color: '#000' }} 
            />
          </Popover>
        )}
        <Typography.Text className={classes.text}>
          {getDisplayText()}
        </Typography.Text>
      </Flex>
      <Skeleton active loading={activity} paragraph={{ rows: 1 }}>
        <Typography.Text className={classes.balance}>
          {(currentMonthSpend)?.toFixed(2)} €
        </Typography.Text>
      </Skeleton>
    </Flex>
  );
};
